import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IUserProfileApi } from './api';
import { UserProfileApi } from './api';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { useMutation, useQueryClient } from 'react-query';
import type { UserProfileControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';
import type { UpdateUserProfileOptionalInfoDto } from '@pflegenavi/shared/api';

const ApiContext = createContext<IUserProfileApi | undefined>(undefined);
const { useApi: useUserProfileApi, ApiProvider: UserProfileApiProvider } =
  makeApiProvider({
    name: 'UserProfile',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new UserProfileApi(tenantId, auth, apiUrl),
  });

export {
  useUserProfileApi,
  UserProfileApiProvider,
  ApiContext as UserProfileApiContext,
};
export type { IUserProfileApi } from './api';

const USER_PROFILE_KEY = (id: string) => [`user-profile`, id];

export const useUserProfile = (
  options: UseQueryOptions<UserProfileControllerResultTypes['getUserProfile']>
): UseQueryResult<UserProfileControllerResultTypes['getUserProfile']> => {
  const { user } = useAuthentication();

  return useApiQuery(
    // eslint-disable-next-line @grncdr/react-hooks/rules-of-hooks
    () => useUserProfileApi(options.enabled === false),
    USER_PROFILE_KEY(user?.userId ?? ''),
    (api) => {
      return api?.getUserProfile() as Promise<
        UserProfileControllerResultTypes['getUserProfile']
      >;
    },
    options
  );
};

export const useUpdateUserProfile = (): UseMutationResult<
  UpdateUserProfileOptionalInfoDto,
  unknown,
  UpdateUserProfileOptionalInfoDto
> => {
  const api = useUserProfileApi();
  const queryClient = useQueryClient();
  const { user } = useAuthentication();

  return useMutation<
    UpdateUserProfileOptionalInfoDto,
    unknown,
    UpdateUserProfileOptionalInfoDto
  >(
    'update_user',
    (data) =>
      api?.updateUser(data) as unknown as Promise<{
        id: string;
      }>,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          USER_PROFILE_KEY(user?.userId ?? '')
        );
      },
    }
  );
};
